import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BellRing, Paperclip, Search, Loader2 } from "lucide-react";

const fmt = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Mahnung erkannt → Originalrechnung suchen und die Mahnung dort als Beilage anhängen (statt neue Rechnung). */
export function InboxReminderLink({ companyId, item, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const rem = item?.extracted?.reminder;
  if (!rem) return null;

  const search = async () => {
    setOpen(true); setLoading(true);
    try { setData((await apiClient.get(`/companies/${companyId}/inbox/${item.id}/reminder-original`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Suche fehlgeschlagen"); }
    finally { setLoading(false); }
  };
  const attach = async (c) => {
    setBusy(c.id);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/attach-to-original`, { target_type: c.type, target_id: c.id });
      toast.success(`Mahnung an ${c.type === "bill" ? "Rechnung" : "Beleg"} ${c.number || ""} angehängt – keine neue Rechnung gebucht`);
      if (r.data?.hint) toast.info(r.data.hint, { duration: 9000 });
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Anhängen fehlgeschlagen"); }
    finally { setBusy(null); }
  };

  return (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-md border border-orange-300 bg-orange-50 px-2.5 py-1.5 text-[11px] text-orange-900" data-testid={`inbox-reminder-hint-${item.id}`}>
        <BellRing className="h-3.5 w-3.5" />
        <span><strong>Mahnung erkannt</strong>{rem.level ? ` (${rem.level}. Mahnung)` : ""}{rem.fee ? ` · Mahngebühr CHF ${fmt(rem.fee)}` : ""}{rem.original_invoice_date ? ` · Rechnung vom ${rem.original_invoice_date}` : ""} — nicht als neue Rechnung buchen, sondern an die Originalrechnung anhängen.</span>
        <button type="button" onClick={search} className="ml-1 inline-flex items-center gap-1 rounded border border-orange-400 bg-white px-1.5 py-0.5 font-medium hover:bg-orange-100" data-testid={`inbox-reminder-search-${item.id}`}>
          <Search className="h-3 w-3" /> Originalrechnung suchen
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="inbox-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing className="h-5 w-5 text-orange-600" /> Originalrechnung zur Mahnung</DialogTitle>
            <DialogDescription>Die Mahnung «{item.file_name}» wird als Beilage angehängt; die Rechnung bleibt wie gebucht. Eine allfällige Mahngebühr wird separat als Aufwand erfasst.</DialogDescription>
          </DialogHeader>
          {loading && <div className="py-6 text-center text-sm text-slate-500"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Suche passende Rechnung…</div>}
          {!loading && data && data.candidates.length === 0 && (
            <div className="py-6 text-center text-sm text-slate-500" data-testid="inbox-reminder-none">Keine passende Rechnung gefunden (Rechnungsnummer, Betrag ± Gebühr, Lieferant). Rechnung zuerst erfassen oder Mahnung normal bestätigen.</div>
          )}
          {!loading && data && data.candidates.map((c, i) => (
            <div key={c.id} className={`flex items-center gap-3 rounded-md border p-2.5 text-sm ${i === 0 ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200"}`} data-testid={`inbox-reminder-candidate-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{c.type === "bill" ? "Rechnung" : "Beleg"} {c.number || "—"} · {c.supplier || "?"}</div>
                <div className="text-xs text-slate-500">{c.date} · CHF {fmt(c.total)} · {c.status}{c.already_has_reminder ? " · hat bereits eine Mahnung" : ""}</div>
                <div className="mt-1 flex flex-wrap gap-1">{c.reasons.map((r) => <Badge key={r} variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">{r}</Badge>)}</div>
              </div>
              <Button size="sm" onClick={() => attach(c)} disabled={!!busy} data-testid={`inbox-reminder-attach-${c.id}`}>
                {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Paperclip className="h-4 w-4 mr-1" /> Als Mahnung anhängen</>}
              </Button>
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
}
